import type { AccountType } from "@/app/(app)/settings/_data";

export type NavAccessKey =
  | "dashboard"
  | "screening"
  | "batch"
  | "monitoring"
  | "settings"
  | "apiSettings";

type NavAccessRule = {
  path: string;
  allowedAccountTypes: AccountType[] | "all";
  requiresMonitoring?: boolean;
  requiresActivePlan?: boolean;
};

export const NAV_ACCESS: Record<NavAccessKey, NavAccessRule> = {
  dashboard: {
    path: "/dashboard",
    allowedAccountTypes: ["owner", "admin", "compliance"],
  },
  screening: {
    path: "/screening-portal",
    allowedAccountTypes: ["owner", "admin", "compliance"],
    requiresActivePlan: true,
  },
  batch: {
    path: "/batch",
    allowedAccountTypes: ["owner", "admin", "compliance", "auditor"],
    requiresActivePlan: true,
  },
  monitoring: {
    path: "/monitoring-portal",
    allowedAccountTypes: ["owner", "admin", "compliance", "auditor"],
    requiresMonitoring: true,
    requiresActivePlan: true,
  },
  settings: { path: "/account-management", allowedAccountTypes: "all" },
  apiSettings: { path: "/api-settings", allowedAccountTypes: ["owner", "admin", "integration"] },
};

const FALLBACK_ORDER: NavAccessKey[] = [
  "dashboard",
  "screening",
  "batch",
  "monitoring",
  "apiSettings",
  "settings",
];

export function isAccountTypeAllowed(
  accountType: string | undefined,
  rule: NavAccessRule,
): boolean {
  if (!accountType) return true;
  if (rule.allowedAccountTypes === "all") return true;
  return rule.allowedAccountTypes.includes(accountType as AccountType);
}

export function resolveFallbackPath(
  accountType: string | undefined,
  enableMonitoring: boolean,
  hasActivePlan: boolean,
): string {
  for (const key of FALLBACK_ORDER) {
    const rule = NAV_ACCESS[key];
    if (rule.requiresMonitoring && !enableMonitoring) continue;
    if (rule.requiresActivePlan && !hasActivePlan) continue;
    if (isAccountTypeAllowed(accountType, rule)) return rule.path;
  }
  return "/help";
}
